import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { useAppDispatch } from "store/index";
import { getPlayerItem, updatePlayerKycStatus } from "store/players/list/slice";
import { ManualKycStatus } from "types/kycStatus";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { handleApiError } from "helpers/handleApiError";

import { getKycStatusLabel } from "./kycStatus";
import styles from "./KycStatusChangeModal.module.scss";

interface Props {
  userId: number;
  currentStatus: ManualKycStatus;
  nextStatus: ManualKycStatus;
  onClose: () => void;
}

const KycStatusChangeModal: React.FC<Props> = ({
  userId,
  currentStatus,
  nextStatus,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setReasonError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        updatePlayerKycStatus({
          userId,
          kycStatus: nextStatus,
          reason: trimmedReason,
        }),
      ).unwrap();
      await dispatch(getPlayerItem(userId));

      dispatch(
        showNotification({
          title: t("success"),
          message: t("kycStatusUpdated"),
          type: NotificationTypes.success,
        }),
      );
      onClose();
    } catch (error) {
      handleApiError({ error, t, dispatch });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      className={styles.modal}
      onClose={handleClose}
      closeOnOverlayClick={!isSubmitting}
      size="sm"
    >
      <Modal.Header className={styles.header}>
        {t("changeKycStatus")}
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <dl className={styles.statuses}>
          <div>
            <dt>{t("currentKycStatus")}:</dt>
            <dd>{getKycStatusLabel(currentStatus, t)}</dd>
          </div>
          <div>
            <dt>{t("newKycStatus")}:</dt>
            <dd>{getKycStatusLabel(nextStatus, t)}</dd>
          </div>
        </dl>
        <InnerInput
          id="player-kyc-status-reason"
          type="textarea"
          label={t("reason")}
          placeholder={t("enterKycStatusReason")}
          value={reason}
          rows={4}
          isRequired
          hasError={reasonError}
          title={reasonError ? t("kycStatusReasonRequired") : undefined}
          disabled={isSubmitting}
          onChange={(value) => {
            setReason(value);
            setReasonError(false);
          }}
        />
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button
          isSecondary
          txt={t("cancel")}
          handleClick={handleClose}
          disabled={isSubmitting}
        />
        <Button
          isPrimary
          txt={t("save")}
          handleClick={handleConfirm}
          disabled={!reason.trim()}
          isLoad={isSubmitting}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default KycStatusChangeModal;
